import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { buildNextLink, getAdsCustomTargeting } from 'site-modules/shared/utils/build-price/build-price';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { BuildPriceEntities, BuildPriceModel, BuildPricePaths } from 'client/data/models/build-price';
import { FloodlightAd } from 'site-modules/shared/components/floodlight/floodlight-ad';
import { RadioList } from 'site-modules/shared/components/radio-list/radio-list';
import { SubmodelsList } from './submodels-list';

import './year-submodel-select.scss';

function YearSubmodelSelectUI({
  modelYear: { id: modelyearId, makeSlug, modelSlug, year },
  yearsSubmodels = null,
  buildPriceFilter,
  setCurrentModelYear,
  pathname,
  isMobile = false,
}) {
  const onYearChange = useCallback(
    ({ target: { value } }) => {
      setCurrentModelYear(value);
    },
    [setCurrentModelYear]
  );

  if (!yearsSubmodels) {
    return null;
  }

  const yearsList = yearsSubmodels.map(({ year: yearName, id }) => ({
    id,
    title: id,
    text: yearName,
    trackingId: 'build_price_choose_year',
    trackingValue: yearName,
    hideValue: true,
  }));

  const submodels =
    yearsSubmodels &&
    get(
      yearsSubmodels.find(({ year: submodelsYear }) => submodelsYear === year),
      'submodels'
    );

  const nextStepButton = get(submodels, 'length') === 1 && (
    <Button
      tag="a"
      color="primary-b"
      size="lg"
      className={classnames(
        'select-trim-btn px-2 py-1 py-md-0_5 text-transform-none d-flex justify-content-center align-items-center',
        {
          'w-100 border-0': isMobile,
        }
      )}
      href={buildNextLink(buildPriceFilter, { modelyearId, submodel: submodels[0].name }, pathname)}
      data-tracking-id="build_price_choose_type"
      data-tracking-value={submodels[0].name}
    >
      Select Trim <i className="icon-arrow-right4 medium ms-0_75" aria-hidden />
    </Button>
  );

  return (
    <div className="year-submodel-select" data-tracking-parent="step1_selecttype">
      {!isMobile && (
        <Row className="pe-lg-2 mb-2">
          <Col xs={12} lg={11} className="text-gray-darker">
            Build your car on Edmunds to see the Edmunds True Market Value (TMV®) - the average price paid for this car
            in your area - and get dealer price quotes and special offers on inventory near you.
          </Col>
        </Row>
      )}
      <Row className="justify-content-center">
        <Col xs={12} lg={9}>
          <div className="d-flex justify-content-between align-items-end mb-2">
            <div>
              <h2 className="heading-4 mb-1_5" data-test="step-heading">
                Model year
              </h2>
              <RadioList
                onChange={onYearChange}
                name="years-list"
                list={yearsList}
                checked={modelyearId}
                ariaLabel="Select a year"
                formClassName="years-list text-gray-darker medium mb-0"
                formGroupClassName="year-option pe-0 d-inline-block mb-0 me-0_75"
                labelContainerClassName="p-0"
                labelCheckedClassName="label-checked text-primary-darker"
                textClassName="year-text d-inline-block fw-normal px-1 py-0_5"
                checkmarkClassName="d-none"
                useInlineTags
              />
            </div>
            {!isMobile && nextStepButton}
          </div>
          <SubmodelsList
            buildPriceFilter={buildPriceFilter}
            pathname={pathname}
            makeSlug={makeSlug}
            modelSlug={modelSlug}
            year={year}
            modelyearId={modelyearId}
            submodels={submodels}
          />
          {isMobile && nextStepButton}
        </Col>
      </Row>
      <FloodlightAd customTargeting={getAdsCustomTargeting(buildPriceFilter.step)} />
    </div>
  );
}

YearSubmodelSelectUI.propTypes = {
  modelYear: BuildPriceEntities.ModelYear.isRequired,
  pathname: PropTypes.string.isRequired,
  buildPriceFilter: BuildPriceEntities.BuildPriceFilter.isRequired,
  setCurrentModelYear: PropTypes.func.isRequired,
  yearsSubmodels: PropTypes.arrayOf(BuildPriceEntities.YearSubmodels),
  isMobile: PropTypes.bool,
};

export const YearSubmodelSelect = connectToModel(YearSubmodelSelectUI, {
  yearsSubmodels: bindToPath(
    ({ modelYear: { makeSlug, modelSlug } }) =>
      BuildPricePaths.buildYearsSubmodelsPath({ make: makeSlug, model: modelSlug }),
    BuildPriceModel
  ),
});
